import { useContext, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { AppRulesContext } from "../../App";
import PlansApi from "../../api/PlansApi";
import PageHeader from "../../components/PageHeader";
import CardLoader from "../../components/content-loader/CardLoader";
import TableLoader from "../../components/content-loader/TableLoader";
import NothingAvailable from "../../components/placeholders/NothingAvailable";
import AFPlansTable from "../../components/tables/AFPlansTable";
import { EActionItem, EToastvariant, IPlansRoot } from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";

const AFPlans = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { rulesSvc } = useContext(AppRulesContext);

  const [assistedFilings, setAFPlans] = useState<IPlansRoot[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  // page index will start from 0 for pagination, and will be inc by 1 in api call
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [currentTimeStamp, setCurrentTimeStamp] = useState<number>(new Date().getTime());
  const [pageCount, setPageCount] = useState(0);
  const [offset, setOffset] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState<IPlansRoot | null>(null);
  const [isPerformingAction, setIsPerformingAction] = useState<EActionItem>(EActionItem.NULL);

  useEffect(() => {
    getAllAFPlans();
  }, [currentTimeStamp]);

  const getAllAFPlans = async () => {
    try {
      setIsLoading(currentPageIndex === 0 && !isPageLoaded);
      setIsFetching(true);
      const plansApi = PlansApi.getInstance();
      const { records, count } = await plansApi.getPlans({
        offset: offset,
        count: 10,
        orderBy: "createdAt",
        order: "DESC",
        type: "assisted"
      });
      setAFPlans(() => records);

      if (records?.length == 1) {
        setOffset(offset - 10);
      }

      setPageCount(Math.ceil(Number(count / 10)));
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsPageLoaded(true);
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  const handleOnPageChange = (event: any) => {
    if (event.selected > 0) {
      setOffset(event.selected * 10);
    } else {
      setOffset(0);
    }
    setCurrentPageIndex(Math.ceil(event.selected));
    setCurrentTimeStamp(new Date().getTime());
  };

  const handleDeletePlan = async () => {
    try {
      if (!showDeleteModal?.uuid) {
        return;
      }
      setIsPerformingAction(EActionItem.DELETE_AF_PLAN);
      let _msg = `${showDeleteModal?.title} deleted successfully`;
      const _values = {
        uuids: [showDeleteModal.uuid]
      };
      const plansApi = PlansApi.getInstance();
      await plansApi.deletePlan(_values);
      getAllAFPlans();
      if (assistedFilings.length === 1) {
        setCurrentPageIndex(currentPageIndex - 1);
      }
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: _msg,
          variant: EToastvariant.Success
        })
      );
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsPerformingAction(EActionItem.NULL);
      setShowDeleteModal(null);
    }
  };

  return (
    <>
      <Container className="w-800">
        {isLoading ? (
          <>
            <PageHeader title="Assisted Filing Plans" />
            <CardLoader cardType="list" />
          </>
        ) : (
          <>
            {!assistedFilings.length ? (
              <>
                <PageHeader title="Assisted Filing Plans" />
                <div className="text-center my-5">
                  <NothingAvailable
                    message="Currently no assisted filing plan is available. Click on add assisted filing button to create."
                    btnTitle="Add Assisted Filing"
                    onBtnPress={() => navigate("/plans/assisted-filings/add")}
                    btnRules={rulesSvc.createPlan}
                  />
                </div>
              </>
            ) : (
              <>
                <PageHeader
                  title="Assisted Filing Plans"
                  primaryBtnTitle="Add New"
                  onPrimaryBtnPress={() => navigate("/plans/assisted-filings/add")}
                  primaryBtnRules={rulesSvc.createPlan}
                />

                {isFetching ? (
                  <TableLoader portalModule="AFPlan" />
                ) : (
                  <AFPlansTable
                    assistedFilingsData={assistedFilings}
                    pageCount={pageCount}
                    forcePage={currentPageIndex}
                    handleOnPageChange={handleOnPageChange}
                    noRecords={!assistedFilings.length}
                    showDeleteModal={showDeleteModal}
                    setShowDeleteModal={setShowDeleteModal}
                    handleDelete={handleDeletePlan}
                    isPerformingAction={isPerformingAction}
                  />
                )}
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default AFPlans;
